import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Antenna, Thermometer, Zap, MapPin, Radio, CheckCircle2, AlertTriangle, Power } from 'lucide-react';
import { api } from '@/lib/api';
import { PageHeader } from '@/components/ui/PageHeader';

type CoaxMasterDetail = {
  id: number;
  name: string | null;
  site: { id: number; name: string } | null;
  model: string;
  nmu_mac: string;
  master_mac: string | null;
  management_address: string;
  nmu_firmware: string | null;
  status: string;
  last_seen_at: string | null;
  env: {
    temperature_celsius?: number;
    vcc_3v3?: number;
    vcore_1v2?: number;
    vdd_1v2?: number;
    vdc_12v?: number;
    eoc_if?: string;
    collected_at?: string;
  } | null;
  eps: Array<{
    id: number;
    mac: string;
    note: string | null;
    model: string | null;
    last_link_status: 'on' | 'off' | null;
    last_diag_at: string | null;
    last_up_rate_mbps: number | null;
    last_dn_rate_mbps: number | null;
    last_up_atten_db: number | null;
    last_dn_atten_db: number | null;
  }>;
};

function fmtAgo(s: string | null): string {
  if (!s) return '—';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  const diff = Math.round((Date.now() - d.getTime()) / 1000);
  if (diff < 60) return `${diff}s`;
  if (diff < 3600) return `${Math.round(diff / 60)} min`;
  if (diff < 86400) return `${Math.round(diff / 3600)} h`;
  return `${Math.round(diff / 86400)} j`;
}

export default function CoaxMasterDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ['coax', 'masters', id],
    queryFn: () => api.get<{ data: CoaxMasterDetail }>(`/coax/masters/${id}`).then((r) => r.data.data),
    refetchInterval: 30_000,
  });

  if (isLoading || !data) return <div className="p-8 text-text-faint">Chargement…</div>;

  const epsOnline = data.eps.filter((e) => e.last_link_status === 'on').length;

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <PageHeader
        title={data.name ?? `Master #${data.id}`}
        subtitle={`${data.model} · ${data.management_address} · ${data.site?.name ?? 'site ?'}`}
        actions={
          <Link to="/app/coax/masters" className="btn btn-ghost btn-sm">
            <ArrowLeft size={12} />Retour
          </Link>
        }
      />

      <div className="flex-1 overflow-auto p-4 space-y-4">
        {/* Status KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <KpiCard
            label="Status"
            value={data.status}
            icon={data.status === 'online' ? CheckCircle2 : AlertTriangle}
            accent={data.status === 'online' ? 'good' : 'warn'}
          />
          <KpiCard
            label="EPs en ligne"
            value={`${epsOnline}/${data.eps.length}`}
            icon={Radio}
            accent={epsOnline === data.eps.length && data.eps.length > 0 ? 'good' : 'neutral'}
          />
          <KpiCard
            label="Température"
            value={data.env?.temperature_celsius != null ? `${data.env.temperature_celsius}°C` : '—'}
            icon={Thermometer}
            accent={data.env?.temperature_celsius != null && data.env.temperature_celsius > 65 ? 'warn' : 'neutral'}
          />
          <KpiCard
            label="Last seen"
            value={fmtAgo(data.last_seen_at)}
            icon={Antenna}
          />
          <KpiCard
            label="EoC interface"
            value={data.env?.eoc_if ?? '—'}
            icon={Radio}
          />
        </div>

        {/* Identity */}
        <div className="card card-pad space-y-2.5">
          <div className="flex items-center gap-2 text-sm font-semibold border-b border-border pb-1.5 mb-2">
            <MapPin size={14} className="text-text-muted" />Identité
          </div>
          <Row label="Name" value={data.name ?? '—'} />
          <Row label="Site" value={data.site?.name ?? '—'} />
          <Row label="Model" value={data.model} />
          <Row label="Management IP" value={data.management_address} mono />
          <Row label="NMU MAC (ethernet)" value={data.nmu_mac} mono />
          <Row label="Master MAC (coax)" value={data.master_mac ?? '—'} mono />
          <Row label="Firmware NMU" value={data.nmu_firmware ?? '—'} />
        </div>

        {/* Environment */}
        <div className="card card-pad space-y-2.5">
          <div className="flex items-center gap-2 text-sm font-semibold border-b border-border pb-1.5 mb-2">
            <Zap size={14} className="text-text-muted" />Environnement (sensor on-board)
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <Mini label="Vcc (3.3V)"  value={data.env?.vcc_3v3   != null ? `${data.env.vcc_3v3} V`   : '—'} />
            <Mini label="Vcore (1.2V)" value={data.env?.vcore_1v2 != null ? `${data.env.vcore_1v2} V` : '—'} />
            <Mini label="Vdd (1.2V)"   value={data.env?.vdd_1v2   != null ? `${data.env.vdd_1v2} V`   : '—'} />
            <Mini label="Vdc (12V)"    value={data.env?.vdc_12v   != null ? `${data.env.vdc_12v} V`   : '—'} />
          </div>
          {data.env?.collected_at ? (
            <div className="text-xs text-text-faint">Capturé : {new Date(data.env.collected_at).toLocaleString('fr-CA')} (il y a {fmtAgo(data.env.collected_at)})</div>
          ) : null}
        </div>

        {/* EPs list */}
        <div className="card overflow-hidden">
          <div className="px-3 py-2 bg-surface-hi text-sm font-semibold border-b border-border flex items-center gap-2">
            <Radio size={14} />EPs derrière ce Master ({data.eps.length})
          </div>
          {data.eps.length === 0 ? (
            <div className="p-6 text-center text-text-faint italic text-sm">Aucun EP détecté</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-xs text-text-muted bg-bg-2">
                <tr>
                  <th className="px-3 py-2 font-medium w-2"></th>
                  <th className="px-3 py-2 font-medium">Note (client)</th>
                  <th className="px-3 py-2 font-medium">MAC</th>
                  <th className="px-3 py-2 font-medium">Modèle</th>
                  <th className="px-3 py-2 font-medium">Diag (Up/Dn Mbps)</th>
                  <th className="px-3 py-2 font-medium">Atten (Up/Dn dB)</th>
                  <th className="px-3 py-2 font-medium">Dernière mesure</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {data.eps.map((e) => (
                  <tr
                    key={e.id}
                    className="hover:bg-surface-hi/40 cursor-pointer"
                    onClick={() => navigate(`/app/coax/endpoints/${e.id}`)}
                  >
                    <td className="px-3 py-2">
                      <Power size={12} className={e.last_link_status === 'on' ? 'text-emerald-400' : 'text-text-faint'} />
                    </td>
                    <td className="px-3 py-2 font-medium">{e.note ?? <span className="text-text-faint italic">—</span>}</td>
                    <td className="px-3 py-2 font-mono text-xs">{e.mac}</td>
                    <td className="px-3 py-2 text-text-muted">{e.model ?? '—'}</td>
                    <td className="px-3 py-2 font-mono text-xs">
                      {e.last_up_rate_mbps != null ? `${e.last_up_rate_mbps} / ${e.last_dn_rate_mbps}` : <span className="text-text-faint">pas mesuré</span>}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">
                      {e.last_up_atten_db != null ? `${e.last_up_atten_db} / ${e.last_dn_atten_db}` : '—'}
                    </td>
                    <td className="px-3 py-2 text-xs text-text-muted">{fmtAgo(e.last_diag_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

function KpiCard({ label, value, icon: Icon, accent }: {
  label: string;
  value: string;
  icon?: typeof Antenna;
  accent?: 'good' | 'warn' | 'neutral';
}) {
  const color = accent === 'good' ? 'text-emerald-400' : accent === 'warn' ? 'text-amber-400' : 'text-text';
  return (
    <div className="card card-pad">
      <div className="flex items-center gap-1.5 text-xs text-text-muted">
        {Icon ? <Icon size={11} /> : null}
        {label}
      </div>
      <div className={`text-sm font-medium ${color}`}>{value}</div>
    </div>
  );
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="grid grid-cols-3 gap-3 text-sm">
      <div className="col-span-1 text-text-muted">{label}</div>
      <div className={`col-span-2 ${mono ? 'font-mono text-xs' : ''}`}>{value}</div>
    </div>
  );
}

function Mini({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs text-text-faint">{label}</div>
      <div className="font-mono text-sm">{value}</div>
    </div>
  );
}
